import type { editor } from 'monaco-editor';

/**
 * TakeShape custom Monaco editor theme
 *
 * Color palette based on TakeShape brand:
 * - Primary: #5c6bc0 (indigo), light: #7c88cc, dark: #5539D2
 * - Secondary: #5CD79B (green)
 * - Text: #455a64 (blue-gray)
 * - App background: #F5F3F9
 * - Error: #E85746
 */

// TakeShape brand colors
const colors = {
  // Primary purple spectrum
  primary: {
    main: '#5c6bc0',
    light: '#7c88cc',
    dark: '#5539D2'
  },
  // Secondary green
  secondary: {
    main: '#5CD79B',
    light: '#C3EAD7',
    dark: '#1ca56d'
  },
  // Semantic colors
  error: '#E85746',
  warning: '#E08E5B',
  // Neutral colors
  text: {
    primary: '#455a64',
    secondary: '#607d8b',
    muted: '#90a4ae'
  },
  background: {
    app: '#F5F3F9',
    editor: '#FAFBFC', // Opaque off-white to blend with app
    widget: '#FFFFFF',
    hover: '#F0EDF5' // Light purple tint for hover states
  },
  border: {
    light: '#E8E4EF',
    main: '#D1C9E0'
  }
};

// Syntax highlighting colors (good contrast, cohesive palette)
const syntax = {
  keyword: '#5539D2', // Dark purple for keywords
  identifier: '#5c6bc0', // Primary purple for identifiers
  property: '#455a64', // Text color for properties
  string: '#1ca56d', // Dark green for strings
  number: '#E08E5B', // Orange for numbers
  comment: '#90a4ae', // Muted gray for comments
  operator: '#607d8b', // Secondary text for operators
  type: '#7c88cc', // Light purple for types
  function: '#5539D2', // Dark purple for functions
  variable: '#455a64', // Text color for variables
  punctuation: '#78909c', // Punctuation
  regexp: '#E85746' // Error red for regex
};

/**
 * TakeShape light theme for Monaco editor
 * Based on VS theme with customizations for TakeShape brand
 */
export const takeshapeLightTheme: editor.IStandaloneThemeData = {
  base: 'vs',
  inherit: true,
  colors: {
    // Editor background and foreground
    'editor.background': colors.background.editor,
    'editor.foreground': colors.text.primary,

    // Editor line numbers
    'editorLineNumber.foreground': colors.text.muted,
    'editorLineNumber.activeForeground': colors.primary.main,

    // Editor cursor
    'editorCursor.foreground': colors.primary.main,

    // Selection colors
    'editor.selectionBackground': '#5c6bc033', // Primary with alpha
    'editor.inactiveSelectionBackground': '#5c6bc01a',
    'editor.selectionHighlightBackground': '#5c6bc01a',

    // Word highlight
    'editor.wordHighlightBackground': '#5CD79B33',
    'editor.wordHighlightStrongBackground': '#5CD79B4d',

    // Find match
    'editor.findMatchBackground': '#5CD79B66',
    'editor.findMatchHighlightBackground': '#5CD79B33',

    // Current line
    'editor.lineHighlightBackground': '#F5F3F9',
    'editor.lineHighlightBorder': '#00000000',

    // Links
    'editorLink.activeForeground': colors.primary.main,
    'textLink.foreground': colors.primary.main,
    'textLink.activeForeground': colors.primary.dark,

    // Scrollbar - subtle but visible
    'scrollbar.shadow': '#00000010',
    'scrollbarSlider.background': '#5c6bc020',
    'scrollbarSlider.hoverBackground': '#5c6bc040',
    'scrollbarSlider.activeBackground': '#5c6bc060',

    // Widget backgrounds (autocomplete, hover, etc.)
    'editorWidget.background': colors.background.widget,
    'editorWidget.border': colors.border.light,
    'editorWidget.resizeBorder': colors.primary.main,

    // Hover widget
    'editorHoverWidget.background': colors.background.widget,
    'editorHoverWidget.border': colors.border.light,

    // Suggest widget (autocomplete)
    'editorSuggestWidget.background': colors.background.widget,
    'editorSuggestWidget.border': colors.border.light,
    'editorSuggestWidget.foreground': colors.text.primary,
    'editorSuggestWidget.selectedBackground': colors.background.hover,
    'editorSuggestWidget.selectedForeground': colors.primary.dark,
    'editorSuggestWidget.highlightForeground': colors.primary.main,
    'editorSuggestWidget.focusHighlightForeground': colors.primary.dark,

    // Sticky scroll - use slightly tinted background so text doesn't overlap when scrolling
    'editorStickyScroll.background': '#FAFBFC',
    'editorStickyScrollHover.background': colors.background.hover,

    // Quick input / command palette
    'quickInput.background': colors.background.widget,
    'quickInput.foreground': colors.text.primary,
    'quickInputList.focusBackground': colors.background.hover,
    'quickInputList.focusForeground': colors.primary.dark,

    // Input boxes
    'input.background': colors.background.widget,
    'input.border': colors.border.main,
    'input.foreground': colors.text.primary,
    'inputOption.activeBorder': colors.primary.main,
    'inputOption.activeBackground': '#5c6bc01a',
    'inputOption.hoverBackground': colors.background.hover,
    focusBorder: colors.primary.main,

    // List colors (used in autocomplete, etc.)
    'list.hoverBackground': colors.background.hover,
    'list.highlightForeground': colors.primary.main,
    'list.focusHighlightForeground': colors.primary.dark,

    // Menu (context menu)
    'menu.background': colors.background.widget,
    'menu.foreground': colors.text.primary,
    'menu.selectionBackground': colors.background.hover,
    'menu.selectionForeground': colors.primary.dark,
    'menu.separatorBackground': colors.border.light,

    // Toolbar
    'toolbar.hoverBackground': colors.background.hover,

    // Picker group
    'pickerGroup.foreground': colors.primary.main,

    // Minimap (if visible)
    'minimap.background': colors.background.editor,
    'minimap.selectionHighlight': '#5c6bc066',

    // Bracket matching
    'editorBracketMatch.background': '#5CD79B33',
    'editorBracketMatch.border': colors.secondary.main,

    // Indentation guides
    'editorIndentGuide.background': colors.border.light,
    'editorIndentGuide.activeBackground': colors.border.main,

    // Rulers
    'editorRuler.foreground': colors.border.light,

    // Gutter
    'editorGutter.background': '#FAFBFC' // Match editor background
  },
  rules: [
    // Comments
    { token: 'comment', foreground: syntax.comment, fontStyle: 'italic' },
    { token: 'comment.block', foreground: syntax.comment, fontStyle: 'italic' },
    { token: 'comment.line', foreground: syntax.comment, fontStyle: 'italic' },

    // Keywords
    { token: 'keyword', foreground: syntax.keyword, fontStyle: 'bold' },
    { token: 'keyword.control', foreground: syntax.keyword },
    { token: 'keyword.operator', foreground: syntax.operator },

    // Strings
    { token: 'string', foreground: syntax.string },
    { token: 'string.key', foreground: syntax.property },
    { token: 'string.value', foreground: syntax.string },
    { token: 'string.key.json', foreground: syntax.identifier },
    { token: 'string.value.json', foreground: syntax.string },

    // Numbers
    { token: 'number', foreground: syntax.number },
    { token: 'number.float', foreground: syntax.number },

    // Types
    { token: 'type', foreground: syntax.type },
    { token: 'type.identifier', foreground: syntax.type },

    // Variables and identifiers
    { token: 'variable', foreground: syntax.variable },
    { token: 'identifier', foreground: syntax.identifier },

    // Functions
    { token: 'function', foreground: syntax.function },
    { token: 'function.call', foreground: syntax.function },

    // Operators
    { token: 'operator', foreground: syntax.operator },
    { token: 'delimiter', foreground: syntax.punctuation },
    { token: 'delimiter.bracket', foreground: syntax.punctuation },

    // Constants
    { token: 'constant', foreground: syntax.number },
    { token: 'constant.language', foreground: syntax.keyword },

    // Regex
    { token: 'regexp', foreground: syntax.regexp },

    // GraphQL-specific tokens
    { token: 'keyword.gql', foreground: syntax.keyword, fontStyle: 'bold' },
    { token: 'type.identifier.gql', foreground: syntax.type },
    { token: 'attribute.name.gql', foreground: syntax.identifier },
    { token: 'argument.identifier.gql', foreground: syntax.identifier },
    { token: 'string.gql', foreground: syntax.string },
    { token: 'number.gql', foreground: syntax.number },
    { token: 'comment.gql', foreground: syntax.comment, fontStyle: 'italic' },

    // JSON-specific
    { token: 'string.key.json', foreground: syntax.identifier },
    { token: 'string.value.json', foreground: syntax.string },
    { token: 'number.json', foreground: syntax.number },
    { token: 'keyword.json', foreground: syntax.keyword } // true, false, null
  ]
};

/**
 * Theme name constant for use throughout the app
 */
export const TAKESHAPE_THEME_NAME = 'takeshape-light';

/**
 * Register the TakeShape theme with Monaco editor
 * Call this before using the theme
 */
export function registerTakeshapeTheme(monaco: typeof import('monaco-editor')): void {
  monaco.editor.defineTheme(TAKESHAPE_THEME_NAME, takeshapeLightTheme);
}
